import { http } from '@/api/client/httpClient';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

// ABP Account module (IProfileAppService) at /api/account/my-profile.
export interface MyProfileDto {
  userName: string;
  email: string;
  name?: string | null;
  surname?: string | null;
  phoneNumber?: string | null;
  hasPassword: boolean;
  isExternal: boolean;
  concurrencyStamp?: string | null;
  extraProperties?: Record<string, unknown>;
}

export interface UpdateMyProfileDto {
  userName: string;
  email: string;
  name?: string | null;
  surname?: string | null;
  phoneNumber?: string | null;
  concurrencyStamp?: string | null;
}

export interface ChangePasswordDto {
  currentPassword?: string;
  newPassword: string;
}

const BASE = '/api/account/my-profile';

export const accountApi = {
  async getMyProfile(): Promise<MyProfileDto> {
    const { data } = await http.get<MyProfileDto>(BASE);
    return data;
  },
  async updateMyProfile(input: UpdateMyProfileDto): Promise<MyProfileDto> {
    const { data } = await http.put<MyProfileDto>(BASE, input);
    return data;
  },
  async changePassword(input: ChangePasswordDto): Promise<void> {
    await http.post(`${BASE}/change-password`, input);
  },
};

const KEY = ['my-profile'] as const;

export function useMyProfile() {
  return useQuery({ queryKey: KEY, queryFn: accountApi.getMyProfile });
}

export function useUpdateMyProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateMyProfileDto) => accountApi.updateMyProfile(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useChangePassword() {
  return useMutation({ mutationFn: (input: ChangePasswordDto) => accountApi.changePassword(input) });
}
